import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { TbBook, TbCalendar } from 'react-icons/tb';

import * as bankAccountService from '../../../services/community/bankAccountService';
import { BookYear } from '../../../types/bankAccount';
import DropdownSelect from '../forms/DropdownSelect';

export type ReportRange =
  | { mode: 'bookYear'; bookYearId: number }
  | { mode: 'dateRange'; fromDate: Date; toDate: Date };

interface BookYearSelectorProps {
  value: ReportRange | null;
  onChange: (range: ReportRange) => void;
  className?: string;
}

const pad = (n: number): string => String(n).padStart(2, '0');
const toInputDate = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export default function BookYearSelector({
  value,
  onChange,
  className,
}: BookYearSelectorProps): JSX.Element | null {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.reports.bookYearSelector' });
  const { t: tCommon } = useTranslation('', { keyPrefix: 'page.financial.reports' });
  const [bookYears, setBookYears] = useState<BookYear[]>([]);
  const [, setLoading] = useState<boolean>(true);

  // Custom date state — initialised from value if mode is 'dateRange'
  const [fromInput, setFromInput] = useState<string>(
    value?.mode === 'dateRange' ? toInputDate(value.fromDate) : '',
  );
  const [toInput, setToInput] = useState<string>(
    value?.mode === 'dateRange' ? toInputDate(value.toDate) : '',
  );

  useEffect(() => {
    if (value?.mode === 'dateRange') {
      setFromInput(toInputDate(value.fromDate));
      setToInput(toInputDate(value.toDate));
    }
  }, [value]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    bankAccountService
      .getBookYears()
      .then((years) => {
        if (cancelled) return;
        const sorted = [...years].sort(
          (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
        );
        setBookYears(sorted);
        if (value == null && sorted.length > 0) {
          const current =
            sorted.find((y) => {
              const start = new Date(y.startDate).getTime();
              const end = new Date(y.endDate).getTime();
              const now = Date.now();
              return start <= now && now <= end;
            }) ?? sorted[0];
          onChange({ mode: 'bookYear', bookYearId: current.bookYearId });
        }
      })
      .catch(() => {
        if (!cancelled) setBookYears([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isCustom = value?.mode === 'dateRange';
  const options = bookYears.map((y) => {
    const start = dayjs(y.startDate);
    const end = dayjs(y.endDate);
    const label = y.label ?? `${start.format('YYYY')}`;
    const range = `${start.format('D MMM YYYY')} → ${end.format('D MMM YYYY')}`;
    return {
      label: `${label}  ·  ${range}`,
      value: y.bookYearId,
    };
  });

  if (isCustom) {
    return (
      <div className={className}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <TbCalendar className="h-5 w-5 text-primary-300" />
            <DropdownSelect<number | string>
              options={[
                { label: t('placeholder'), value: '__placeholder__' },
                ...options,
                { label: `▶ ${t('customRange') ?? 'Custom range'}`, value: '__custom__' },
              ]}
              setValue={(v) => {
                if (v === '__custom__') return;
                if (v === '__placeholder__') return;
                if (typeof v === 'number') {
                  onChange({ mode: 'bookYear', bookYearId: v });
                }
              }}
              value={undefined}
              placeholder={t('placeholder')}
              className="min-w-[260px]"
            />
          </div>
          <input
            type="date"
            value={fromInput}
            onChange={(e) => {
              const next = e.target.value;
              setFromInput(next);
              if (next && toInput && next <= toInput) {
                onChange({
                  mode: 'dateRange',
                  fromDate: new Date(next),
                  toDate: new Date(toInput),
                });
              }
            }}
            className="h-[50px] rounded-[8px] border-2 border-secondary-200 bg-secondary-200/50 px-3 font-medium text-primary-900 focus:border-primary-300 focus:outline-none"
          />
          <span className="text-secondary-300">→</span>
          <input
            type="date"
            value={toInput}
            onChange={(e) => {
              const next = e.target.value;
              setToInput(next);
              if (fromInput && next && fromInput <= next) {
                onChange({
                  mode: 'dateRange',
                  fromDate: new Date(fromInput),
                  toDate: new Date(next),
                });
              }
            }}
            className="h-[50px] rounded-[8px] border-2 border-secondary-200 bg-secondary-200/50 px-3 font-medium text-primary-900 focus:border-primary-300 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => {
              const today = new Date();
              const monthAgo = new Date();
              monthAgo.setMonth(today.getMonth() - 1);
              if (fromInput && fromInput.length > 0 && toInput) {
                onChange({
                  mode: 'dateRange',
                  fromDate: new Date(fromInput),
                  toDate: new Date(toInput),
                });
              } else {
                onChange({ mode: 'dateRange', fromDate: monthAgo, toDate: today });
              }
            }}
            className="text-primary-700 ml-1 text-xs font-medium underline-offset-2 hover:underline">
            {tCommon('reset', { defaultValue: 'Reset' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <TbBook className="h-5 w-5 text-primary-300" />
        <DropdownSelect<number | string>
          options={[
            ...options,
            { label: `▶ ${t('customRange') ?? 'Custom range'}`, value: '__custom__' },
          ]}
          setValue={(v) => {
            if (v === '__custom__') {
              const today = new Date();
              const monthAgo = new Date();
              monthAgo.setMonth(today.getMonth() - 1);
              setFromInput(toInputDate(monthAgo));
              setToInput(toInputDate(today));
              onChange({ mode: 'dateRange', fromDate: monthAgo, toDate: today });
              return;
            }
            if (typeof v === 'number') onChange({ mode: 'bookYear', bookYearId: v });
          }}
          value={value?.mode === 'bookYear' ? value.bookYearId : undefined}
          placeholder={t('placeholder')}
          className="min-w-[280px]"
        />
      </div>
    </div>
  );
}
